import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Button,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Stack,
  Avatar
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Restaurant as RecipeIcon,
  Assignment as SetupIcon,
  TrendingUp,
  AttachMoney,
  FileDownload,
  Refresh
} from '@mui/icons-material';
import { motion } from 'framer-motion';

// Import tab components
import BasicSetupTab from './components/BasicSetupTab';
import RecipeSetupTab from './components/RecipeSetupTab';

// Import services
import inventoryService from '../services/inventoryService';
import menuService from '../../../services/menuService';
import ingredientsService from '../services/ingredientsService';

const MotionDiv = motion.div;

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`stock-setup-tabpanel-${index}`}
      aria-labelledby={`stock-setup-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const StockSetup = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State management
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data states
  const [menuItems, setMenuItems] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [recipeData, setRecipeData] = useState([]);
  const [summary, setSummary] = useState({
    totalPreMadeItems: 0,
    totalRecipes: 0,
    avgRecipeCost: 0,
    totalInventoryValue: 0
  });

  // Calculate summary statistics
  const calculateSummary = useCallback((stockItems, recipes) => {
    const preMadeItems = stockItems.filter(item => !item.is_recipe);
    const recipeItems = recipes || [];
    
    const avgCost = recipeItems.length > 0 
      ? recipeItems.reduce((sum, recipe) => sum + (recipe.total_cost || 0), 0) / recipeItems.length 
      : 0;
    
    const totalValue = [
      ...preMadeItems.map(item => (item.current_quantity || 0) * (item.cost_per_unit || 0)),
      ...recipeItems.map(recipe => (recipe.total_cost || 0))
    ].reduce((sum, value) => sum + value, 0);

    setSummary({
      totalPreMadeItems: preMadeItems.length,
      totalRecipes: recipeItems.length,
      avgRecipeCost: avgCost,
      totalInventoryValue: totalValue
    });
  }, []);

  const fetchRecipes = useCallback(async () => {
    try {
      console.log('🔄 Fetching recipes with ingredients...');
      // FIXED: Use the correct method name
      const response = await inventoryService.getRecipeItems();
      const recipes = response?.data || response || [];
      console.log('✅ Recipes fetched with ingredients:', recipes);
      return recipes;
    } catch (error) {
      console.error('❌ Error fetching recipes:', error);
      return [];
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Loading stock setup data...');

      // ✅ FIXED: Use correct menuService method name
      const menuResponse = await menuService.getAllWithCategories();
      const menuData = menuResponse?.data || menuResponse || [];
      
      // Fetch ingredients
      const ingredientsResponse = await ingredientsService.getIngredients();
      const ingredientsData = ingredientsResponse?.data || ingredientsResponse || [];
      
      // Fetch existing stock setup
      const stockResponse = await inventoryService.getStock();
      const stockItems = stockResponse?.data || stockResponse || [];
      
      // ✅ FIXED: Fetch existing recipes properly
      const recipes = await fetchRecipes();

      setMenuItems(menuData);
      setIngredients(ingredientsData);
      setStockData(stockItems);
      setRecipeData(recipes); // ✅ This should now contain your recipe

      // Calculate summary
      calculateSummary(stockItems, recipes);
      
      console.log('✅ Stock setup data loaded successfully', {
        menuItems: menuData.length,
        ingredients: ingredientsData.length,
        stockItems: stockItems.length,
        recipes: recipes.length
      });
    } catch (err) {
      console.error('❌ Error loading stock setup data:', err);
      setError(err.message || 'Failed to load stock setup data');
    } finally {
      setLoading(false);
    }
  }, [fetchRecipes, calculateSummary]);

  // Load data on component mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Handle export (placeholder - implement based on requirements)
  const handleExport = async (format) => {
    try {
      console.log(`📤 Exporting stock setup data as ${format}...`);
      // TODO: Implement export functionality
      setSuccess(t('inventory.exportSuccess'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Export failed:', err);
      setError(t('inventory.exportError'));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              <SetupIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {t('inventory.setup.title') || 'Stock Setup'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('inventory.setup.subtitle') || 'Configure pre-made items and recipe ingredients'}
              </Typography>
            </Box>
          </Box>
          
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchData}
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              {t('common.refresh')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={() => handleExport('csv')}
              sx={{ borderRadius: 2 }}
            >
              {t('common.export')}
            </Button>
          </Stack>
        </Box>
      </MotionDiv>

      {/* Summary Cards */}
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <InventoryIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('inventory.setup.totalPreMadeItems') || 'Pre-made Items'}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {summary.totalPreMadeItems}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <RecipeIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('inventory.setup.totalRecipes') || 'Recipe Items'}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {summary.totalRecipes}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'warning.main' }}>
                    <AttachMoney />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('inventory.setup.avgRecipeCost') || 'Avg Recipe Cost'}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      ${summary.avgRecipeCost.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <TrendingUp />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t('inventory.setup.totalInventoryValue') || 'Total Value'}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      ${summary.totalInventoryValue.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </MotionDiv>

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main Content with Tabs */}
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <Card sx={{ borderRadius: 3 }}>
          {/* Tab Headers */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  minHeight: { xs: 64, md: 72 },
                  '&.Mui-selected': {
                    color: 'primary.main'
                  }
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0'
                }
              }}
            >
              <Tab
                icon={<InventoryIcon />}
                label={
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {t('inventory.setup.basicSetup') || 'Basic Setup'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('inventory.setup.basicSetupDesc') || 'Pre-made items with vendor costs'}
                    </Typography>
                  </Box>
                }
                iconPosition="start"
                sx={{ 
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 1, md: 2 }
                }}
              />
              <Tab
                icon={<RecipeIcon />}
                label={
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {t('inventory.setup.recipeSetup') || 'Recipe Setup'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('inventory.setup.recipeSetupDesc') || 'Link ingredients to menu items'}
                    </Typography>
                  </Box>
                }
                iconPosition="start"
                sx={{ 
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 1, md: 2 }
                }}
              />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <TabPanel value={currentTab} index={0}>
            <BasicSetupTab
              menuItems={menuItems}
              stockData={stockData}
              onDataChange={fetchData}
              onSuccess={setSuccess}
              onError={setError}
            />
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <RecipeSetupTab
              menuItems={menuItems}
              ingredients={ingredients}
              recipeData={recipeData}
              onDataChange={fetchData}
              onSuccess={setSuccess}
              onError={setError}
            />
          </TabPanel>
        </Card>
      </MotionDiv>
    </Box>
  );
};

export default StockSetup;